import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowSquareOut, Broom, CheckCircle, Flask, Play, Printer, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { mktApi, errMsg } from '@/pages/marketing/marketingApi';

const ACTORS = {
  sales: { label: '営業', cls: 'bg-fuchsia-100 text-fuchsia-700' },
  mfg: { label: '製造', cls: 'bg-blue-100 text-blue-700' },
  shizai: { label: '資材', cls: 'bg-cyan-100 text-cyan-700' },
  keiri: { label: '経理', cls: 'bg-emerald-100 text-emerald-700' },
};

const buildSteps = (m) => [
  {
    no: 1, actors: ['mfg'], title: '品目マスタ登録',
    desc: '販売する完成品と、その材料を品目マスタに登録します。品番(SKU)はテンプレートから発番し、原価・単位・発注点を設定します。',
    artifact: m && `${m.items.product.name}（${m.items.product.sku}）＋ 材料2点`,
    links: [{ to: '/mfg/inventory', label: '品目・在庫を開く' }],
  },
  {
    no: 2, actors: ['mfg'], title: 'BOM（部品表）登録',
    desc: '完成品1個をつくるのに必要な構成部品と数量を定義します。製造オーダー発行時に材料の所要量が自動計算されます。',
    artifact: m && 'BN粉末 0.5kg ＋ 焼結治具 1個 / 完成品1個',
    links: [{ to: '/mfg/boms', label: 'BOM一覧を開く' }],
  },
  {
    no: 3, actors: ['sales'], title: '客先マスタ登録・販売単価',
    desc: '締め日・敬称・支払条件を持つ客先を登録します。販売単価マスタに契約単価を入れておくと、見積で自動適用されます。',
    artifact: m && `${m.customer.name}（締め日: 末日 / 契約単価 ¥5,800）`,
    links: [{ to: '/marketing/customers', label: '客先マスタを開く' }],
  },
  {
    no: 4, actors: ['sales'], title: '受注の作成（下書き）',
    desc: '客先・希望納期・明細（品目×数量）を入力して受注を作成します。明細ごとに「在庫出荷」か「製造」かを選べます。',
    order: m?.orders?.draft,
  },
  {
    no: 5, actors: ['sales'], title: '見積計算 → 見積書の発行・印刷',
    desc: '原価×マージンまたは販売単価マスタで見積を計算し、見積書（スナップショット）を発行します。マージンが基準未満なら上長承認ゲートがかかります。',
    order: m?.orders?.quoted,
    print: m && { id: m.docs.quotation.id, label: `見積書 ${m.docs.quotation.doc_no} を印刷` },
  },
  {
    no: 6, actors: ['sales'], title: '客先承認の記録',
    desc: '客先からの注文（メール・FAX等）を受けたら、承認方法と証憑を記録します。ここから製造への引き渡しが可能になります。',
    order: m?.orders?.approved,
  },
  {
    no: 7, actors: ['sales', 'mfg'], title: '製造依頼 → 製造オーダー発行',
    desc: '「製造」明細はSeizoに製造オーダー（WO）として自動発行されます。BOMから材料所要量が展開され、進捗は受注に自動同期されます。',
    order: m?.orders?.in_production,
    artifact: m && `製造オーダー ${m.work_order.wo_no}${m.work_order.manufacturing_no ? `（製番 ${m.work_order.manufacturing_no}）` : ''}`,
    links: [{ to: '/mfg/work-orders', label: '製造オーダーを開く' }],
  },
  {
    no: 8, actors: ['shizai'], title: '資材調達（発注依頼）',
    desc: '材料が不足する場合、資材アプリに発注依頼を出します。承認フローを経て発注書が発行され、仕入先へ送られます。',
    artifact: m && `発注依頼 ${m.shizai_request.request_no}（BN粉末 5kg）`,
    links: [{ to: '/shizai/requests', label: '発注依頼を開く' }, { to: '/shizai/orders', label: '発注一覧を開く' }],
  },
  {
    no: 9, actors: ['shizai', 'mfg'], title: '材料の入庫・在庫管理',
    desc: '納品された材料を入庫するとロットが作られ、在庫が増えます。製造・出荷で使うとFIFOで引き落とされます。',
    artifact: m && 'デモ在庫: 完成品20個 / BN粉末10kg 入庫済',
    links: [{ to: '/mfg/inventory', label: '在庫を開く' }],
  },
  {
    no: 10, actors: ['mfg'], title: '検査（QC）',
    desc: '製造完了または在庫出荷の受注はSeizoの出荷管理に並びます。検査に合格させると出荷準備完了になります。',
    order: m?.orders?.qc_pending,
    links: [{ to: '/mfg/shipping', label: 'Seizo 出荷管理を開く' }],
  },
  {
    no: 11, actors: ['mfg'], title: '出荷（分納対応）',
    desc: '明細ごとに出荷数を入力して出荷します。一部だけ出荷すると「一部出荷」となり残数を管理、出荷分の在庫が引き落とされます。',
    order: m?.orders?.partially_shipped,
    artifact: m && '4/10 出荷済 — Seizo出荷管理で「残りを出荷」を実際に試せます',
    links: [{ to: '/mfg/shipping', label: 'Seizo 出荷管理を開く' }],
  },
  {
    no: 12, actors: ['sales'], title: '売上確定',
    desc: '全量出荷されると「売上確定待ち」になります。Technoaで完成入力（売上計上）した内容を記録すると受注が完了します。',
    order: m?.orders?.completion_pending,
    artifact: m && 'この受注で「売上確定」ボタンを実際に試せます',
  },
  {
    no: 13, actors: ['keiri'], title: '締め請求（月次請求書）',
    desc: '客先の締め日で当月の売上確定済み受注をまとめ、1枚の月次請求書を発行・印刷します。CSVでの一括出力も可能です。',
    artifact: m && `${m.month} 締め分 請求書 ${m.docs.monthly_invoice.doc_no}（未入金 — 入金記録を試せます）`,
    print: m && { id: m.docs.monthly_invoice.id, label: `請求書 ${m.docs.monthly_invoice.doc_no} を印刷` },
    links: [{ to: '/marketing/billing', label: '締め請求を開く' }],
  },
  {
    no: 14, actors: ['keiri'], title: '入金記録 → 領収書（月次締めの完了）',
    desc: '入金を記録すると領収書が自動発行されます。未入金はダッシュボードのKPIで常に監視でき、これで受注→請求→入金のループが閉じます。',
    artifact: m && `前月分 ${m.docs.monthly_invoice_prev.doc_no} は入金済 → 領収書 ${m.docs.receipt.doc_no}`,
    print: m && { id: m.docs.receipt.id, label: `領収書 ${m.docs.receipt.doc_no} を印刷` },
    links: [{ to: '/marketing/billing', label: '締め請求を開く' }],
  },
];

export default function Demo() {
  const [manifest, setManifest] = useState(null);
  const [tmActive, setTmActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const s = await mktApi.demoStatus();
      setManifest(s.seeded ? s : null);
      setTmActive(!!s.test_mode_active);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setBusy(true);
    try {
      const m = await mktApi.demoSeed();
      setManifest(m);
      setTmActive(true);
      if (m.test_mode?.auto_enabled) {
        toast.success('テストモード（あなた専用のサンドボックス）を自動で有効にし、デモデータを作成しました', { duration: 6000 });
        setTimeout(() => window.location.reload(), 1200);
      } else {
        toast.success('デモデータを作成しました');
      }
    } catch (e) { toast.error(errMsg(e, 'デモデータの作成に失敗しました')); }
    finally { setBusy(false); }
  };

  const endTestMode = async () => {
    if (!window.confirm('テストモードを終了しますか？サンドボックス内の変更（デモデータ含む）はすべて破棄され、本番データに戻ります。')) return;
    setBusy(true);
    try {
      await mktApi.demoEnd();
      toast.success('テストモードを終了しました。本番データに戻ります');
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const wipe = async () => {
    if (!window.confirm('デモデータ（【デモ】と付く客先・受注・品目など）をすべて削除しますか？')) return;
    setBusy(true);
    try {
      await mktApi.demoWipe();
      setManifest(null);
      toast.success('デモデータを削除しました');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const seeded = !!manifest;
  const steps = buildSteps(manifest);

  return (
    <div className="space-y-5" data-testid="mkt-demo-page">
      <div className="rounded-lg border border-fuchsia-200 bg-zinc-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-zinc-900">
              <Sparkle size={22} weight="fill" className="text-fuchsia-500" /> デモモード — 受注から月次締めまでの流れ
            </h1>
            <p className="mt-1.5 text-xs leading-relaxed text-zinc-600">
              品目・BOMの登録 → 営業受注 → 見積書 → 製造オーダー → 資材調達 → 出荷 → 売上確定 → 締め請求・入金・領収書まで、
              業務の一連の流れを実データで体験できます。各ステップの「画面を開く」「印刷」から実際の画面・帳票を確認してください。
            </p>
            <p className="mt-1.5 text-[11px] text-zinc-500">
              デモ開始時に<b>テストモード（あなた専用のサンドボックス）が自動で有効</b>になります。デモ中の操作は本番データに一切影響しません。
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {tmActive && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-300 px-2.5 py-1 text-[11px] font-semibold text-orange-700" data-testid="mkt-demo-tm-badge">
                  <Flask size={13} weight="fill" /> テストモード中（サンドボックス）
                </span>
              )}
              {seeded && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-700" data-testid="mkt-demo-seeded-badge">
                  <CheckCircle size={13} weight="fill" /> デモデータ作成済み（客先: {manifest.customer.name}）
                </span>
              )}
            </div>
            {!seeded && !loading && (
              <p className="mt-2 text-[11px] text-zinc-400" data-testid="mkt-demo-not-seeded">
                デモデータは未作成です。「デモを開始」を押すと各ステップの実画面を開けるようになります。
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
              <Button onClick={seed} disabled={busy}
                className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-demo-seed-btn">
                <Play size={15} className="mr-1.5" /> {busy ? '処理中…' : seeded ? 'デモデータを再作成' : 'デモを開始'}
              </Button>
              {seeded && (
                <Button variant="outline" onClick={wipe} disabled={busy}
                  className="text-red-600 border-red-200 hover:bg-red-50" data-testid="mkt-demo-wipe-btn">
                  <Broom size={15} className="mr-1.5" /> デモデータ削除
                </Button>
              )}
              {tmActive && (
                <Button variant="outline" onClick={endTestMode} disabled={busy}
                  className="text-orange-700 border-orange-300 hover:bg-orange-50" data-testid="mkt-demo-end-tm-btn">
                  <Flask size={15} className="mr-1.5" /> テストモードを終了
                </Button>
              )}
            </div>
        </div>
        {seeded && (
          <p className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-[10px] text-amber-700">
            デモデータ（【デモ】表記）はサンドボックス内の受注一覧・ダッシュボード・在庫などに表示されます。
            確認が終わったら「テストモードを終了」で変更ごとまとめて破棄できます。
          </p>
        )}
      </div>

      <ol className="relative space-y-3 pl-10">
        <span className="absolute left-[15px] top-4 bottom-4 w-px bg-teal-700" aria-hidden />
        {steps.map((s) => (
          <StepCard key={s.no} step={s} seeded={seeded} />
        ))}
      </ol>
    </div>
  );
}

function StepCard({ step, seeded }) {
  return (
    <li className="relative rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:shadow" data-testid={`mkt-demo-step-${step.no}`}>
      <span className="absolute -left-10 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white border-2 border-fuchsia-400 font-mono text-xs font-bold text-fuchsia-700">
        {step.no}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-bold text-zinc-900">{step.title}</h2>
        {step.actors.map((a) => (
          <span key={a} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ACTORS[a].cls}`}>{ACTORS[a].label}</span>
        ))}
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-zinc-500">{step.desc}</p>
      {seeded && (step.artifact || step.order) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {step.order && (
            <span className="rounded bg-zinc-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-zinc-600">
              {step.order.order_no}（{step.order.status_label}）
            </span>
          )}
          {step.artifact && <span className="text-[11px] text-fuchsia-600">{step.artifact}</span>}
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {step.order && (
          <Link to={`/marketing/orders/${step.order.id}`} data-testid={`mkt-demo-order-link-${step.no}`}>
            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!seeded}>
              <ArrowSquareOut size={12} className="mr-1" /> 受注 {step.order.order_no} を開く
            </Button>
          </Link>
        )}
        {(step.links || []).map((l) => (
          <Link key={l.to} to={l.to}>
            <Button variant="outline" size="sm" className="h-7 text-xs">
              <ArrowSquareOut size={12} className="mr-1" /> {l.label}
            </Button>
          </Link>
        ))}
        {step.print && (
          <Button variant="outline" size="sm" className="h-7 text-xs text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50"
            disabled={!seeded} onClick={() => window.open(`/marketing/print/${step.print.id}`, '_blank')}
            data-testid={`mkt-demo-print-${step.no}`}>
            <Printer size={12} className="mr-1" /> {step.print.label}
          </Button>
        )}
        {!seeded && (step.order || step.print) && (
          <span className="self-center text-[10px] text-zinc-300">（デモデータ作成後に開けます）</span>
        )}
      </div>
    </li>
  );
}
